import { Head, Link, router } from "@inertiajs/react"
import { useRef, useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Props {
  case_record: CaseRecord
}

export default function New({ case_record }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Photos", href: `/cases/${case_record.id}/photos` },
    { title: "Add", href: `/cases/${case_record.id}/photos/new` },
  ]

  const [caption, setCaption] = useState("")
  const [files, setFiles] = useState<FileList | null>(null)
  const [processing, setProcessing] = useState(false)
  const [previews, setPreviews] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files
    setFiles(selected)

    // Generate previews
    if (selected) {
      const urls: string[] = []
      for (const file of selected) {
        urls.push(URL.createObjectURL(file))
      }
      setPreviews(urls)
    } else {
      setPreviews([])
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!files || files.length === 0) return

    setProcessing(true)
    const formData = new FormData()
    formData.append("caption", caption)
    for (const file of files) {
      formData.append("files[]", file)
    }

    router.post(`/cases/${case_record.id}/photos`, formData, {
      forceFormData: true,
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Photo - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Photo</h1>

        <form onSubmit={handleSubmit} className="max-w-xl">
          <div className="mb-4">
            <label htmlFor="files" className="mb-1 block font-medium">
              Select Photos
            </label>
            <input
              ref={fileRef}
              type="file"
              id="files"
              multiple
              accept="image/png, image/jpeg"
              onChange={handleFileChange}
              required
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
            <p className="text-hpi-med-gray mt-1 text-xs">
              PNG and JPEG files only. Multiple files allowed.
            </p>
          </div>

          {previews.length > 0 && (
            <div className="mb-4 grid grid-cols-4 gap-2">
              {previews.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`Preview ${i + 1}`}
                  className="h-24 w-full rounded border object-cover"
                />
              ))}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="caption" className="mb-1 block font-medium">
              Caption (optional)
            </label>
            <input
              type="text"
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={50}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing || !files || files.length === 0}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Uploading..." : "Add"}
            </button>
            <Link
              href={`/cases/${case_record.id}/photos`}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
